import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { PlatformClient } from "../../platform/supabase";
import { roleLabel } from "../../platform/locale";
import "./AdminOrganizationERP.css";

type OrganizationRow={
  organizationId:string;
  type:string;
  code?:string|null;
  name:string;
  status:string;
  parentOrganizationId?:string|null;
  parentName?:string|null;
  memberCount:number;
  learnerCount:number;
};

type RoleAssignment={
  roleAssignmentId:string;
  roleKey:string;
  scopeType:string;
  scopeRef?:string|null;
  validFrom:string;
  validUntil?:string|null;
  active:boolean;
};

type LearnerAssignment={
  assignmentId:string;
  learnerId:string;
  learnerName:string;
  assignmentType:string;
  status:string;
  validFrom:string;
  validUntil?:string|null;
};

type Membership={
  membershipId:string;
  personId:string;
  displayName:string;
  email?:string|null;
  identityLinked:boolean;
  membershipType:string;
  status:string;
  validFrom:string;
  validUntil?:string|null;
  version:number;
  roles:RoleAssignment[];
  learnerAssignments:LearnerAssignment[];
};

type Invitation={
  invitationId:string;
  expectedEmail?:string|null;
  roleKeys:string[];
  status:string;
  expiresAt:string;
  createdAt:string;
  claimedAt?:string|null;
};

type LearnerOption={
  learnerId:string;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
};

type Directory={
  organization:{
    organizationId:string;
    name:string;
    type:string;
    status:string;
    parentOrganizationId?:string|null;
  };
  memberships:Membership[];
  invitations:Invitation[];
  learners:LearnerOption[];
};

type CreatedInvite={
  invitationId:string;
  claimUrl:string;
  token:string;
  expectedEmail?:string|null;
  roleKeys:string[];
  expiresAt:string;
};

function formatDate(value?:string|null){
  if(!value) return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function organizationTypeLabel(type:string){
  if(type==="SCHOOL") return "학교";
  if(type==="EDUCATION_OFFICE") return "교육청";
  if(type==="INSTITUTION") return "기관";
  if(type==="PLATFORM") return "플랫폼";
  return type;
}

function openOrganization360(organizationId:string){
  window.history.pushState({},"",`/organizations?organization=${encodeURIComponent(organizationId)}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function AdminAccessPage({client}:{client:PlatformClient}){
  const [organizations,setOrganizations]=useState<OrganizationRow[]>([]);
  const [organizationId,setOrganizationId]=useState("");
  const [directory,setDirectory]=useState<Directory|null>(null);
  const [selectedMembershipId,setSelectedMembershipId]=useState("");
  const [memberQuery,setMemberQuery]=useState("");
  const [memberView,setMemberView]=useState<"ALL"|"ACTIVE"|"UNLINKED">("ALL");
  const [expectedEmail,setExpectedEmail]=useState("");
  const [inviteRoles,setInviteRoles]=useState<string[]>(["CAREER_TEACHER"]);
  const [createdInvite,setCreatedInvite]=useState<CreatedInvite|null>(null);
  const [learnerId,setLearnerId]=useState("");
  const [assignmentType,setAssignmentType]=useState("CAREER_SUPPORT");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    client.rpc("admin_organization_directory").then(({data,error})=>{
      if(!active) return;
      if(error){
        setError(error.message);
        return;
      }
      const rows=(data || []) as OrganizationRow[];
      setOrganizations(rows);
      const requested=new URLSearchParams(window.location.search).get("organization") || "";
      setOrganizationId((current)=>
        requested && rows.some((item)=>item.organizationId===requested)
          ? requested
          : rows.some((item)=>item.organizationId===current)
            ? current
            : rows.find((item)=>item.type==="SCHOOL" && item.status==="ACTIVE")?.organizationId
              || rows.find((item)=>["INSTITUTION","EDUCATION_OFFICE"].includes(item.type) && item.status==="ACTIVE")?.organizationId
              || ""
      );
    });
    return()=>{active=false;};
  },[client,refresh]);

  useEffect(()=>{
    if(!organizationId){
      setDirectory(null);
      return;
    }
    let active=true;
    client.rpc("admin_access_directory",{p_organization_id:organizationId}).then(({data,error})=>{
      if(!active) return;
      if(error){
        setError(error.message);
        return;
      }
      const next=data as Directory;
      setDirectory(next);
      setSelectedMembershipId((current)=>
        next.memberships.some((item)=>item.membershipId===current)
          ? current
          : next.memberships.find((item)=>item.status==="ACTIVE")?.membershipId
            || next.memberships[0]?.membershipId
            || ""
      );
      setLearnerId((current)=>
        next.learners.some((item)=>item.learnerId===current)
          ? current
          : next.learners[0]?.learnerId || ""
      );
      setInviteRoles(next.organization.type==="SCHOOL" ? ["CAREER_TEACHER"] : next.organization.type==="PLATFORM" ? ["PLATFORM_ADMIN"] : ["REGIONAL_ADMIN"]);
    });
    return()=>{active=false;};
  },[client,organizationId,refresh]);

  const selected=useMemo(
    ()=>directory?.memberships.find((item)=>item.membershipId===selectedMembershipId) || null,
    [directory,selectedMembershipId]
  );

  const selectedOrganization=useMemo(
    ()=>organizations.find((item)=>item.organizationId===organizationId) || null,
    [organizations,organizationId]
  );

  const visibleMemberships=useMemo(()=>{
    const keyword=memberQuery.trim().toLowerCase();
    return (directory?.memberships || []).filter((item)=>{
      if(memberView==="ACTIVE" && item.status!=="ACTIVE") return false;
      if(memberView==="UNLINKED" && (item.status!=="ACTIVE" || item.identityLinked)) return false;
      if(!keyword) return true;
      return [item.displayName,item.email || "",...item.roles.map((role)=>roleLabel(role.roleKey))]
        .some((value)=>value.toLowerCase().includes(keyword));
    });
  },[directory,memberQuery,memberView]);

  const accessStats=useMemo(()=>{
    const memberships=directory?.memberships || [];
    const invitations=directory?.invitations || [];
    return {
      active:memberships.filter((item)=>item.status==="ACTIVE").length,
      linked:memberships.filter((item)=>item.status==="ACTIVE" && item.identityLinked).length,
      unlinked:memberships.filter((item)=>item.status==="ACTIVE" && !item.identityLinked).length,
      openInvites:invitations.filter((item)=>item.status==="OPEN").length
    };
  },[directory]);

  const allowedRoles=directory?.organization.type==="SCHOOL"
    ? ["CAREER_TEACHER","SCHOOL_ADMIN"]
    : directory?.organization.type==="PLATFORM"
      ? ["PLATFORM_ADMIN"]
      : ["REGIONAL_ADMIN","PLATFORM_ADMIN"];

  async function run(key:string, action:()=>PromiseLike<{error?:{message:string}|null}>){
    setBusy(key);
    setError("");
    try{
      const result=await action();
      if(result.error) throw new Error(result.error.message);
      setRefresh((value)=>value+1);
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
    }finally{
      setBusy("");
    }
  }

  async function createInvite(){
    setBusy("invite");
    setError("");
    const {data,error}=await client.rpc("admin_create_staff_invitation",{
      p_organization_id:organizationId,
      p_expected_email:expectedEmail.trim() || null,
      p_role_keys:inviteRoles,
      p_valid_minutes:10080
    });
    setBusy("");

    if(error){
      setError(error.message);
      return;
    }

    setCreatedInvite(data as CreatedInvite);
    setRefresh((value)=>value+1);
  }

  function toggleInviteRole(role:string){
    setInviteRoles((current)=>
      current.includes(role)
        ? current.filter((item)=>item!==role)
        : [...current,role]
    );
  }

  return (
    <section className="admin-access-page">
      <header className="access-erp-hero">
        <div>
          <p className="eyebrow">IDENTITY & ACCESS ERP</p>
          <h2>계정·권한 운영</h2>
          <p>기관별 구성원 초대부터 로그인 연결, 역할, 담당 학생, 소속 종료까지 하나의 계정 운영 흐름으로 관리합니다.</p>
        </div>
        {organizationId && <button className="secondary" onClick={()=>openOrganization360(organizationId)}>기관 360 보기</button>}
      </header>

      <section className="access-erp-context">
        <div className="access-erp-selector">
          <label>관리 기관
            <select value={organizationId} onChange={(event)=>{
              setOrganizationId(event.target.value);
              setCreatedInvite(null);
              setMemberQuery("");
              setMemberView("ALL");
            }}>
              {organizations.filter((item)=>item.status==="ACTIVE" && ["SCHOOL","INSTITUTION","EDUCATION_OFFICE","PLATFORM"].includes(item.type)).map((item)=>(
                <option key={item.organizationId} value={item.organizationId}>
                  {item.name} · {organizationTypeLabel(item.type)}
                </option>
              ))}
            </select>
          </label>
          {selectedOrganization && (
            <div className="access-erp-org-meta">
              <span className={`org-erp-type ${selectedOrganization.type.toLowerCase()}`}>{organizationTypeLabel(selectedOrganization.type)}</span>
              <strong>{selectedOrganization.name}</strong>
              <small>{selectedOrganization.code || "코드 없음"}{selectedOrganization.parentName ? ` · ${selectedOrganization.parentName}` : ""}</small>
            </div>
          )}
        </div>

        <div className="access-erp-kpis">
          <AccessKpi label="활성 구성원" value={accessStats.active} />
          <AccessKpi label="로그인 연결" value={accessStats.linked} />
          <AccessKpi label="미연결 계정" value={accessStats.unlinked} attention={accessStats.unlinked>0} />
          <AccessKpi label="열린 초대" value={accessStats.openInvites} attention={accessStats.openInvites>0} />
        </div>
      </section>

      {error && <p className="error">{error}</p>}

      {directory && (
        <>
          <section className="staff-invite-card">
            <div>
              <p className="eyebrow">Access Invitation</p>
              <h3>구성원 초대</h3>
              <p>초대받은 사용자가 Google 로그인 후 1회 Claim하면 Membership과 승인된 역할이 생성됩니다.</p>
            </div>

            <div className="staff-invite-form">
              <label>초대 이메일
                <input value={expectedEmail} onChange={(event)=>setExpectedEmail(event.target.value)} placeholder="teacher@example.com" required />
              </label>
              <div className="invite-role-options">
                {allowedRoles.map((role)=>(
                  <label key={role}>
                    <input type="checkbox" checked={inviteRoles.includes(role)} onChange={()=>toggleInviteRole(role)} />
                    {roleLabel(role)}
                  </label>
                ))}
              </div>
              <button className="primary compact-button" disabled={busy!=="" || inviteRoles.length===0 || !expectedEmail.trim()} onClick={createInvite}>
                {busy==="invite" ? "발급 중…" : "7일 초대 링크 발급"}
              </button>
            </div>

            {createdInvite && (
              <div className="access-invite-created">
                <QRCodeSVG value={createdInvite.claimUrl} size={152} level="M" />
                <div>
                  <strong>초대 링크가 생성됐습니다.</strong>
                  <p>{createdInvite.expectedEmail || "이메일 제한 없음"} · {createdInvite.roleKeys.map(roleLabel).join(", ")}</p>
                  <small>만료 {formatDate(createdInvite.expiresAt)}</small>
                  <div className="wallet-button-row">
                    <button className="secondary" onClick={()=>navigator.clipboard.writeText(createdInvite.claimUrl)}>링크 복사</button>
                    <a className="verify-link-button" href={createdInvite.claimUrl} target="_blank" rel="noreferrer">초대 열기</a>
                  </div>
                </div>
              </div>
            )}
          </section>

          <div className="access-workspace">
            <aside className="access-member-list access-member-list-upgraded">
              <div className="panel-title"><strong>구성원</strong><span>{visibleMemberships.length}/{directory.memberships.length}명</span></div>
              <div className="access-member-tools">
                <input value={memberQuery} onChange={(event)=>setMemberQuery(event.target.value)} placeholder="이름·이메일·역할 검색" />
                <div>
                  <button className={memberView==="ALL" ? "active" : ""} onClick={()=>setMemberView("ALL")}>전체</button>
                  <button className={memberView==="ACTIVE" ? "active" : ""} onClick={()=>setMemberView("ACTIVE")}>활성</button>
                  <button className={memberView==="UNLINKED" ? "active" : ""} onClick={()=>setMemberView("UNLINKED")}>미연결</button>
                </div>
              </div>
              {visibleMemberships.map((item)=>(
                <button key={item.membershipId} className={selectedMembershipId===item.membershipId ? "access-member-row active" : "access-member-row"}
                  onClick={()=>setSelectedMembershipId(item.membershipId)}>
                  <div className="access-member-row-head">
                    <strong>{item.displayName}</strong>
                    <span className={item.identityLinked ? "access-link-state linked" : "access-link-state"}>{item.identityLinked ? "연결" : "미연결"}</span>
                  </div>
                  <span>{item.email || "이메일 미연결"}</span>
                  <small>{item.status==="ACTIVE" ? "활성" : "종료"} · {item.roles.filter((role)=>role.active).map((role)=>roleLabel(role.roleKey)).join(", ") || "역할 없음"}</small>
                </button>
              ))}
              {!visibleMemberships.length && <p className="case-empty">조건에 맞는 구성원이 없습니다.</p>}
            </aside>

            <div className="access-member-detail">
              {selected ? (
                <>
                  <header className="access-member-head">
                    <div>
                      <p className="eyebrow">MEMBERSHIP 360</p>
                      <h2>{selected.displayName}</h2>
                      <p>{selected.email || "이메일 미연결"} · {selected.membershipType}</p>
                      <div className="access-member-badges">
                        <span className={selected.identityLinked ? "good" : "attention"}>{selected.identityLinked ? "로그인 연결" : "로그인 미연결"}</span>
                        <span>{selected.roles.filter((role)=>role.active).length}개 활성 역할</span>
                        {directory.organization.type==="SCHOOL" && <span>{selected.learnerAssignments.filter((item)=>item.status==="ACTIVE").length}명 담당</span>}
                      </div>
                    </div>
                    <span className={selected.status==="ACTIVE" ? "status-pill good" : "status-pill"}>{selected.status==="ACTIVE" ? "활성" : "종료"}</span>
                  </header>

                  <section className="access-detail-section">
                    <div className="panel-title"><strong>역할</strong><span>Scope 서버 검증</span></div>
                    <div className="access-role-list">
                      {selected.roles.map((role)=>(
                        <div className="access-role-row" key={role.roleAssignmentId}>
                          <div>
                            <strong>{roleLabel(role.roleKey)}</strong>
                            <span>{role.scopeType}{role.scopeRef ? ` · ${role.scopeRef}` : ""}</span>
                          </div>
                          {role.active ? (
                            <button className="secondary danger" disabled={busy!==""} onClick={()=>run(`end-role-${role.roleAssignmentId}`,()=>client.rpc("admin_end_role_assignment",{
                              p_role_assignment_id:role.roleAssignmentId,
                              p_reason:"Admin에서 역할 종료"
                            }))}>역할 종료</button>
                          ):<span className="status-pill">종료됨</span>}
                        </div>
                      ))}
                    </div>

                    {selected.status==="ACTIVE" && (
                      <div className="access-add-row">
                        {allowedRoles.filter((role)=>!selected.roles.some((item)=>item.roleKey===role && item.active)).map((role)=>(
                          <button className="secondary" key={role} disabled={busy!==""} onClick={()=>run(`grant-${role}`,()=>client.rpc("admin_grant_membership_role",{
                            p_membership_id:selected.membershipId,
                            p_role_key:role
                          }))}>{roleLabel(role)} 추가</button>
                        ))}
                      </div>
                    )}
                  </section>

                  {directory.organization.type==="SCHOOL" && (
                    <section className="access-detail-section">
                      <div className="panel-title"><strong>학생 담당배정</strong><span>{selected.learnerAssignments.filter((item)=>item.status==="ACTIVE").length}명 활성</span></div>

                      {selected.status==="ACTIVE" && directory.learners.length>0 && (
                        <div className="assignment-create-row">
                          <select value={learnerId} onChange={(event)=>setLearnerId(event.target.value)}>
                            {directory.learners.map((learner)=>(
                              <option key={learner.learnerId} value={learner.learnerId}>
                                {learner.displayName}{learner.gradeLevel ? ` · ${learner.gradeLevel}학년` : ""}{learner.classLabel ? ` ${learner.classLabel}반` : ""}
                              </option>
                            ))}
                          </select>
                          <select value={assignmentType} onChange={(event)=>setAssignmentType(event.target.value)}>
                            <option value="CAREER_SUPPORT">진로지원</option>
                            <option value="HOMEROOM">담임</option>
                            <option value="COUNSELING">상담</option>
                            <option value="COACHING">코칭</option>
                            <option value="REVIEW">검토</option>
                          </select>
                          <button className="primary compact-button" disabled={busy!=="" || !learnerId} onClick={()=>run("assign-learner",()=>client.rpc("admin_assign_learner",{
                            p_membership_id:selected.membershipId,
                            p_learner_id:learnerId,
                            p_assignment_type:assignmentType
                          }))}>학생 배정</button>
                        </div>
                      )}

                      <div className="assignment-list">
                        {selected.learnerAssignments.map((item)=>(
                          <div className="access-role-row" key={item.assignmentId}>
                            <div>
                              <strong>{item.learnerName}</strong>
                              <span>{item.assignmentType}</span>
                            </div>
                            {item.status==="ACTIVE" ? (
                              <button className="secondary danger" disabled={busy!==""} onClick={()=>run(`end-assignment-${item.assignmentId}`,()=>client.rpc("admin_end_learner_assignment",{
                                p_assignment_id:item.assignmentId,
                                p_reason:"Admin에서 학생 담당 종료"
                              }))}>담당 종료</button>
                            ):<span className="status-pill">종료됨</span>}
                          </div>
                        ))}
                        {!selected.learnerAssignments.length && <p className="case-empty">학생 담당배정이 없습니다.</p>}
                      </div>
                    </section>
                  )}

                  {selected.status==="ACTIVE" && (
                    <section className="membership-danger-zone">
                      <div>
                        <strong>Membership 종료</strong>
                        <p>이 Organization의 활성 역할과 학생 담당배정도 함께 종료됩니다.</p>
                      </div>
                      <button className="secondary danger" disabled={busy!==""} onClick={()=>run("end-membership",()=>client.rpc("admin_end_membership",{
                        p_membership_id:selected.membershipId,
                        p_expected_version:selected.version,
                        p_reason:"Admin에서 소속 종료"
                      }))}>소속 종료</button>
                    </section>
                  )}
                </>
              ):<p className="case-empty">구성원을 선택하세요.</p>}
            </div>
          </div>

          <section className="invitation-history">
            <div className="panel-title"><strong>초대 이력</strong><span>{directory.invitations.length}건</span></div>
            {directory.invitations.map((invite)=>(
              <div className="review-line" key={invite.invitationId}>
                <div>
                  <strong>{invite.expectedEmail || "이메일 제한 없음"}</strong>
                  <span>{invite.roleKeys.map(roleLabel).join(", ")}</span>
                  <small>만료 {formatDate(invite.expiresAt)}</small>
                </div>
                <div className="wallet-button-row">
                  <span className={invite.status==="CLAIMED" ? "status-pill good" : "status-pill"}>{invite.status}</span>
                  {invite.status==="OPEN" && (
                    <button className="secondary danger" disabled={busy!==""} onClick={()=>run(`revoke-invite-${invite.invitationId}`,()=>client.rpc("admin_revoke_staff_invitation",{
                      p_invitation_id:invite.invitationId,
                      p_reason:"Admin에서 초대 취소"
                    }))}>초대 취소</button>
                  )}
                </div>
              </div>
            ))}
          </section>
        </>
      )}
    </section>
  );
}


function AccessKpi({label,value,attention=false}:{label:string;value:number;attention?:boolean}){
  return (
    <div className={attention ? "access-erp-kpi attention" : "access-erp-kpi"}>
      <span>{label}</span>
      <strong>{value.toLocaleString("ko-KR")}</strong>
    </div>
  );
}
